/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo } from 'react';
import { flowRight, isUndefined } from 'lodash';
import { GET_ENTER_DATA } from 'common/constants/inputs';
import RowLabelTooltip from 'components/RowLabelTooltip/RowLabelTooltip';
import { useRevealEllipsisInTooltip } from 'services/hooks';
import { formatNumbers, toString } from 'utilities';
import { ValueViewerProps } from './ValueViewer.props';
import withAction from '../../../WithAction';
import withDialog from '../../../WithDialog';
import withLink from '../../../WithLink';
import withRequired from '../../../WithRequired';
import withTooltip from '../../../WithTooltip';

const ValueViewer = ({ cell, selected, onDoubleClick }: ValueViewerProps) => {
  const { tooltipRef, width } = useRevealEllipsisInTooltip(cell.width);

  const keyUpHandler = (event: KeyboardEvent) => {
    const key = event.key || (event as any).keyCode;
    if (selected && key && key.toUpperCase() === 'F2') {
      onDoubleClick?.();
    }
  };

  const spanId = useMemo(() => {
    if (cell.extraAttr) {
      return `${cell.extraAttr}-${cell.alias}-${cell.key}_viewer`;
    }

    return `${cell.alias}-${cell.key}_viewer`;
  }, [cell]);

  useEffect(() => {
    document.addEventListener('keyup', keyUpHandler);
    return () => document.removeEventListener('keyup', keyUpHandler);
  }, [selected]);

  const value = useMemo(() => {
    if (
      ((!isUndefined(cell.value) && !cell.hidden) || cell.value)
      && cell.gridType
      && (cell.gridType === 'number' || cell.gridType === 'percentage')
      // eslint-disable-next-line no-restricted-globals
      && !isNaN(cell.value)
    ) {
      if (!cell.readOnly && !toString(cell.value).length) {
        return cell.enterDataMessage || GET_ENTER_DATA(cell.isRequired!, cell.enterDataMessage ?? '');
      }
      // Override number of decimals and display the entire number to highlight the errors
      if (cell.allowDecimals === false && cell.tooltipMessages?.length && cell.tooltipMessages.length > 0)
        return cell.value;
      return formatNumbers(cell as any);
    }
    if (!cell.readOnly && !cell.hidden && !cell.value && cell.placeholder) {
      return cell.placeholder;
    }

    return cell.value;
  }, [cell]);

  const valueOrNA = useMemo(() => (cell.value !== null ? value : 'N/A'), [cell, value]);

  if (
    (['title', 'approachName'].includes(cell.colAlias!) || cell.className?.includes('table-header'))
    && !cell.defaultTooltip
  ) {
    return (
      <span className="value-viewer" ref={tooltipRef as any}>
        <RowLabelTooltip title={value} containerWidth={width}>
          <span id={spanId} className={`text-cell ${cell.className ?? ''}`}>
            {value}
          </span>
        </RowLabelTooltip>
      </span>
    );
  }

  return (
    <span className="value-viewer">
      <span id={spanId} className={`text-cell ${cell.className ?? ''}`}>
        {!cell.displayNAforNull ? value : valueOrNA}
      </span>
    </span>
  );
};

// flowRight is a compose method alternative
const enhance = flowRight(withTooltip, withDialog, withRequired, withLink, withAction);

export default enhance(ValueViewer);
